import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/cn";

/**
 * RedlineSpan — inline span for tracked-changes / suggestion / comment-anchor
 * marks inside a document. Renders as a `<span>` by default and inherits the
 * surrounding text flow (no block-level shift).
 *
 * Reads the diff / review-preview token set:
 *   --ins / --ins-bg   insertion (green)
 *   --del / --del-bg   deletion (red strikethrough)
 * plus `--warning` for the comment-anchor marker (the amber comment hue).
 *
 * Variants tell the reader whether the change is from a deterministic source
 * (`insertion`, `deletion` — already accepted) or pending judgement
 * (`suggestion` — dotted underline indicates an AI proposal). `comment-anchor`
 * is a non-destructive marker that ties text to a comment thread.
 *
 * Use `data-sug-id` (or `data-edit-id`) for click delegation: a parent
 * document handler can read the dataset to open the right reasoning popover
 * or comment thread.
 *
 * Composition: `asChild` swaps the `<span>` for a Radix Slot — useful when
 * the consumer's clause renderer already wraps text in `<em>` / `<strong>` /
 * other inline elements.
 *
 * Motion: the only animated property is `transition-colors`, gated with
 * `motion-reduce:transition-none` so users who prefer reduced motion get an
 * instant color change rather than a fade.
 */
const redlineSpanVariants = cva(
  "transition-colors duration-180 ease-snappy motion-reduce:transition-none",
  {
    variants: {
      variant: {
        insertion:
          "bg-ins-bg/60 text-ins underline decoration-ins/60 underline-offset-2",
        deletion:
          "bg-del-bg/50 text-del line-through decoration-del/70 decoration-2",
        suggestion:
          "bg-ins-bg/30 text-ins underline decoration-dotted decoration-ins/80 underline-offset-2 cursor-pointer hover:bg-ins-bg/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "comment-anchor":
          "bg-warning/10 text-foreground underline decoration-warning decoration-2 underline-offset-2 cursor-pointer hover:bg-warning/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      },
    },
    defaultVariants: { variant: "insertion" },
  },
);

export interface RedlineSpanProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof redlineSpanVariants> {
  /**
   * Render as a Radix Slot, merging classes onto the child element. Useful when
   * the consumer's clause renderer already wraps text in another inline tag.
   */
  asChild?: boolean;
  /**
   * AI-suggestion identifier — surfaces as `data-sug-id` for click delegation
   * to the consumer's reasoning popover handler.
   */
  suggestionId?: string;
  /**
   * Human-edit identifier — surfaces as `data-edit-id` for click delegation
   * to the consumer's comment popover.
   */
  editId?: string;
}

export const RedlineSpan = React.forwardRef<HTMLSpanElement, RedlineSpanProps>(
  (
    { className, variant, asChild = false, suggestionId, editId, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "span";
    return (
      <Comp
        ref={ref as React.Ref<HTMLElement>}
        data-sug-id={suggestionId}
        data-edit-id={editId}
        className={cn(redlineSpanVariants({ variant }), className)}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
RedlineSpan.displayName = "RedlineSpan";

export { redlineSpanVariants };
